import { Livewire, Alpine } from '../../vendor/livewire/livewire/dist/livewire.esm';
import confetti from 'canvas-confetti';
window.confetti = confetti;

import Focus from "@alpinejs/focus";
import Tooltip from "@ryangjchandler/alpine-tooltip";
import mask from "@alpinejs/mask";
import "cropperjs";
import Clipboard from "@ryangjchandler/alpine-clipboard";
import AlpineFloatingUI from "@awcodes/alpine-floating-ui";
import wizard from '@glhd/alpine-wizard';
import Axios from 'axios';
import intersect from '@alpinejs/intersect';
import Collapse from '@alpinejs/collapse';

// Team Trips v2 Alpine modules
import heroCrossfade    from './team-trips/hero-crossfade.js';
import customsStamps    from './team-trips/customs-stamps.js';
import marquee          from './team-trips/marquee.js';
import mobileDrawer     from './team-trips/mobile-drawer.js';
import stepObserver     from './team-trips/intersection-observer.js';

import Honeybadger from '@honeybadger-io/js';
import {
    buildDiagnostic,
    fingerprintFor,
    collectRestorableInputs,
    applyRestorableInputs,
    shouldReloadForExpiry,
    EXPIRED_INPUT_STORAGE_KEY,
    EXPIRED_RELOAD_GUARD_KEY,
    PRESERVED_INPUT_TTL_MS,
} from './livewire-419.js';
import { configureHoneybadger } from './honeybadger-filter.js';
import { patchDialogShowModal } from './dialog-show-modal-patch.js';
import { registerRatingRollback, RATING_REQUEST_FAILED_EVENT } from './livewire-rating-rollback.js';

if (! import.meta.env.VITE_APP_ENV) {
  throw new Error('VITE_APP_ENV is not set. This is required for Honeybadger to work.\nTry adding VITE_APP_ENV=$APP_ENV to your .env file.');
}

// Predicate list + match logic live in honeybadger-filter.js so they can be
// unit tested under `node --test`. Each suppressed pattern documents WHY
// we don't want it reported — see that file before adding/removing entries.
configureHoneybadger(Honeybadger, {
  apiKey: 'hbp_dqJ95Gwxgh3D3w32qp9XagVq8A9Plu0uY2mY',
  developmentEnvironments: ['local', 'testing'],
  environment: import.meta.env.VITE_APP_ENV,
});

Alpine.magic('honeybadger', () => Honeybadger);

Alpine.plugin(AlpineFloatingUI);
Alpine.plugin(Clipboard);
Alpine.plugin(Tooltip);
Alpine.plugin(Focus);
Alpine.plugin(mask);
Alpine.plugin(wizard);
Alpine.plugin(intersect);
Alpine.plugin(Collapse);

window.Alpine = Alpine;
window.Axios = Axios;

patchDialogShowModal();

document.addEventListener('livewire:init', () => {
    registerRatingRollback(Livewire, (detail) => window.dispatchEvent(new CustomEvent(RATING_REQUEST_FAILED_EVENT, { detail })));

    /**
     * Intercept failed Livewire requests and show a user-friendly banner
     * instead of the raw Laravel error page.
     *
     * 419 - Session expired (CSRF token mismatch after long idle periods).
     * 403/404/422 - Deliberate server-side refusals, shown but not reported.
     * 5xx - Unexpected server errors.
     */
    // A 419 ("Page Expired") means this tab's CSRF/session token is no longer
    // valid, so every subsequent request - including background wire:poll
    // commits - will keep failing. These one-shot flags ensure we report the
    // expiry to Honeybadger once per page load (not once per failed poll) and
    // kick off recovery a single time, instead of stacking notices/reloads.
    let livewire419Reported = false;
    let livewire419RecoveryStarted = false;

    Livewire.hook('request', ({ uri, payload, respond, fail }) => {
        // respond() fires as soon as the fetch Response is received (before
        // succeed/fail), so this is the only reliable place to capture the
        // X-Request-Id header for both success and failure paths.
        let serverRequestId = null;
        respond(({ response }) => {
            serverRequestId = response?.headers?.get?.('X-Request-Id') ?? null;
        });
        fail(({ status, content, preventDefault }) => {
            if (status === 419) {
                preventDefault();

                const diagnostic = buildDiagnostic(payload, {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    content,
                });

                console.error('[Livewire 419]', diagnostic);

                // Report the expiry once per page load. This previously fired on
                // every failed request, so a single stale tab whose wire:poll
                // kept firing could push thousands of identical notices. Bucket
                // by component, falling back to the URL path when the snapshot
                // can't be parsed, so admin and public 419s stop collapsing into
                // one "unknown" fault.
                if (!livewire419Reported) {
                    livewire419Reported = true;
                    Honeybadger.notify(new Error('Livewire 419'), {
                        name: 'Livewire419Error',
                        fingerprint: fingerprintFor(diagnostic.component, diagnostic.url),
                        context: diagnostic,
                        tags: ['livewire', '419'],
                    });
                }

                // Recover quietly rather than popping a blocking "out of sync"
                // dialog. recoverFromLivewireExpiry() and the livewire:navigated
                // handler below preserve and restore the user's input across the
                // reload.
                if (livewire419RecoveryStarted) return;
                livewire419RecoveryStarted = true;
                recoverFromLivewireExpiry();
                return;
            }

            // Deliberate server-side refusals. Unhandled, they fall through every branch
            // below and the click becomes a no-op with no message. Not reported to
            // Honeybadger — an authorization denial is the system working, not a fault.
            if (status === 403 || status === 404 || status === 422) {
                // Banner first, preventDefault() only once it has rendered: suppressing
                // Livewire's own handling before the banner exists would leave a silent
                // dead click if the banner threw.
                try {
                    showLivewireErrorBanner(
                        buildErrorDetails({ status, content, uri, payload, serverRequestId })
                    );
                    preventDefault();
                } catch (bannerError) {
                    console.error('[Livewire refusal banner failed]', bannerError);
                }

                return;
            }

            if (status >= 500 || status === 0) {
                preventDefault();
                const details = buildErrorDetails({ status, content, uri, payload, serverRequestId });
                // Only 500 carries a PHP exception/trace worth reporting from the client.
                // 502/503/504 are gateway failures (Vapor cold starts, concurrency
                // ceilings, upstream timeouts) with no JS context, and any real
                // server-side exception is already captured by Honeybadger on the
                // backend — reporting them here just duplicates noise.
                // Status 0 fires on every user-initiated abort (tab close, back
                // button, link click mid-request) and has no signal.
                if (status === 500) {
                    reportToHoneybadger(details);
                }
                showLivewireErrorBanner(details);
            }
        });
    });
});

/**
 * Recover from a Livewire 419 ("Page Expired") without nagging the user.
 *
 * The token is dead, so we reload to obtain a fresh one. Before reloading we
 * stash any non-empty form input to sessionStorage and restore it once the new
 * page is ready, making the reload invisible and lossless for the field values
 * currently in the DOM. (Multi-step component state held server-side can't
 * survive a full reload - only the visible inputs are preserved.) A short
 * cooldown prevents reload loops if the replacement page also 419s.
 */
function recoverFromLivewireExpiry() {
    const now = Date.now();

    let lastReloadAt = null;
    try {
        const storedReloadAt = sessionStorage.getItem(EXPIRED_RELOAD_GUARD_KEY);
        lastReloadAt = storedReloadAt === null ? null : Number(storedReloadAt);
    } catch (e) {
        // sessionStorage can throw in privacy mode; fall through and reload.
    }

    if (!shouldReloadForExpiry(now, lastReloadAt)) {
        // We just reloaded for a 419 and it didn't take - stop rather than loop.
        return;
    }

    try {
        const fields = collectRestorableInputs(document);
        if (Object.keys(fields).length > 0) {
            sessionStorage.setItem(
                EXPIRED_INPUT_STORAGE_KEY,
                JSON.stringify({ path: window.location.pathname, at: now, fields }),
            );
        }
    } catch (e) {
        console.warn('[Livewire 419] Could not preserve form input before reload:', e);
    }

    try {
        sessionStorage.setItem(EXPIRED_RELOAD_GUARD_KEY, String(now));
    } catch (e) {
        // Non-fatal: without the guard we simply lose reload-loop protection.
    }

    window.location.reload();
}

/**
 * Restore form input stashed by recoverFromLivewireExpiry() after the page has
 * reloaded with a fresh token. Runs on livewire:navigated (fired on first load
 * and after each wire:navigate) so Filament/Livewire fields exist and are bound.
 * The stash is single-use and scoped to its origin path with a 2-minute TTL.
 */
function restoreExpiredFormInput() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(EXPIRED_INPUT_STORAGE_KEY);
        if (raw !== null) {
            sessionStorage.removeItem(EXPIRED_INPUT_STORAGE_KEY);
        }
    } catch (e) {
        // sessionStorage can throw in privacy mode; nothing to restore.
        return;
    }
    if (raw === null) return;

    let blob = null;
    try {
        blob = JSON.parse(raw);
    } catch (e) {
        // A corrupt stash is a bug, not an expected browser restriction - surface it.
        console.warn('[Livewire 419] Could not parse preserved input stash:', e);
        return;
    }

    if (!blob || blob.path !== window.location.pathname) return;
    if (typeof blob.at !== 'number' || Date.now() - blob.at > PRESERVED_INPUT_TTL_MS) return;

    try {
        applyRestorableInputs(document, blob.fields);
    } catch (e) {
        console.warn('[Livewire 419] Could not restore preserved form input:', e);
    }
}

document.addEventListener('livewire:navigated', restoreExpiredFormInput);

/**
 * Collect everything we can reasonably surface about a failed Livewire request.
 * The server response is parsed opportunistically - Laravel emits JSON with
 * `message`/`exception`/`file`/`line` for API-ish routes and an HTML error page
 * otherwise.
 */
function buildErrorDetails({ status, content, uri, payload, serverRequestId }) {
    return {
        status,
        statusText: statusTextFor(status),
        referenceId: serverRequestId ?? generateReferenceId(),
        referenceIdSource: serverRequestId ? 'server' : 'client',
        timestamp: new Date().toISOString(),
        uri: uri ?? null,
        pageUrl: window.location.href,
        component: extractComponentName(payload),
        calls: extractComponentCalls(payload),
        server: extractServerMessage(content),
        userAgent: navigator.userAgent,
    };
}

function statusTextFor(status) {
    return {
        0: 'Network Error',
        // Refusals, not faults: "Server Error" would blame the system for a
        // deliberate decision and send the user to support instead of to the
        // lead who closed the roster.
        403: 'Not Allowed',
        404: 'Not Found',
        422: 'Not Accepted',
        500: 'Internal Server Error',
        502: 'Bad Gateway',
        503: 'Service Unavailable',
        504: 'Gateway Timeout',
    }[status] ?? 'Server Error';
}

/**
 * Last-resort client-side ref, used only when the server never set
 * X-Request-Id (status 0: network error / request aborted). These refs
 * cannot be correlated against server logs - the request never reached
 * the server - so support should treat them as "client-only" context.
 */
function generateReferenceId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    const time = Date.now().toString(36).slice(-4).toUpperCase();
    return `${time}-${rand}`;
}

function extractComponentName(payload) {
    try {
        const snapshot = payload?.components?.[0]?.snapshot;
        if (!snapshot) {
            return null;
        }
        const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
        return parsed?.memo?.name ?? null;
    } catch (e) {
        // Only swallow JSON parse errors (the snapshot string is opaque and may
        // change shape between Livewire versions). Let real bugs surface.
        if (!(e instanceof SyntaxError)) {
            throw e;
        }
        return null;
    }
}

/**
 * Flatten the Livewire calls array into `method(arg, arg)` strings.
 * This is the most useful repro hint - it names the exact action that
 * was in flight when the failure happened.
 */
function extractComponentCalls(payload) {
    const components = payload?.components ?? [];
    const calls = [];
    for (const component of components) {
        for (const call of component?.calls ?? []) {
            const method = call?.method ?? call?.path ?? 'unknown';
            const args = Array.isArray(call?.params)
                ? call.params.map(summarizeCallArg).join(', ')
                : '';
            calls.push(`${method}(${args})`);
        }
    }
    return calls;
}

function summarizeCallArg(value) {
    if (value === null || value === undefined) {
        return String(value);
    }
    if (typeof value === 'string') {
        return value.length > 40 ? JSON.stringify(value.slice(0, 37) + '...') : JSON.stringify(value);
    }
    if (typeof value === 'object') {
        try {
            const json = JSON.stringify(value);
            return json.length > 60 ? json.slice(0, 57) + '...' : json;
        } catch (e) {
            // JSON.stringify throws TypeError on circular references; anything
            // else is a real bug.
            if (!(e instanceof TypeError)) {
                throw e;
            }
            return '[object]';
        }
    }
    return String(value);
}

/**
 * Forward the error to Honeybadger tagged with the banner's reference id
 * so support can paste the ref a user reports and jump straight to the trace.
 * fingerprint groups repeated failures of the same component+status together.
 */
function reportToHoneybadger(details) {
    try {
        const summary = details.server?.exception
            ? `${details.server.exception}: ${details.server.message}`
            : (details.server?.message ?? `HTTP ${details.status} ${details.statusText}`);

        Honeybadger.notify(new Error(`Livewire request failed - ${summary}`), {
            name: 'LivewireRequestFailed',
            fingerprint: `livewire:${details.component ?? 'unknown'}:${details.status}`,
            context: {
                referenceId: details.referenceId,
                referenceIdSource: details.referenceIdSource,
                status: details.status,
                statusText: details.statusText,
                requestUri: details.uri,
                pageUrl: details.pageUrl,
                component: details.component,
                calls: details.calls,
                serverException: details.server?.exception ?? null,
                serverMessage: details.server?.message ?? null,
                serverFile: details.server?.file ?? null,
                serverLine: details.server?.line ?? null,
                timestamp: details.timestamp,
            },
        });
    } catch (err) {
        // Surface reporting failures to devtools so a broken Honeybadger.notify
        // (bad config, SDK update) doesn't go invisible. Never rethrow - the
        // banner itself must still render.
        console.error('[Honeybadger] Failed to report Livewire error:', err);
    }
}

function extractServerMessage(content) {
    if (typeof content !== 'string' || content === '') {
        return null;
    }
    try {
        const parsed = JSON.parse(content);
        if (parsed && typeof parsed === 'object' && parsed.message) {
            return {
                message: String(parsed.message),
                exception: parsed.exception ? String(parsed.exception) : null,
                file: parsed.file ? String(parsed.file) : null,
                line: parsed.line ?? null,
            };
        }
    } catch (e) {
        // Only fall back to HTML parsing when JSON.parse failed; let other
        // errors propagate so we see real bugs.
        if (!(e instanceof SyntaxError)) {
            throw e;
        }
        // Prefer the explicit error-page marker (rendered by layouts/guest.blade.php
        // for 4xx/5xx error pages). The layout <title> is unreliable - it falls
        // back to the site name ("All State Game" / "EXACT Sports") when a child
        // view forgets to forward $title, producing meaningless reports.
        const errorMarker = content.match(/<meta[^>]+name=["']x-error-page["'][^>]*>/i);
        if (errorMarker) {
            const statusMatch = errorMarker[0].match(/content=["'](\d+)["']/i);
            const titleMatch = content.match(/<title[^>]*>([^<]+)<\/title>/i);
            const status = statusMatch ? statusMatch[1] : '???';
            const title = titleMatch ? titleMatch[1].trim() : 'Server Error';
            return { message: `HTTP ${status} error page: ${title}`, exception: null, file: null, line: null };
        }
        const titleMatch = content.match(/<title[^>]*>([^<]+)<\/title>/i);
        if (titleMatch) {
            return { message: titleMatch[1].trim(), exception: null, file: null, line: null };
        }
    }
    return null;
}

/**
 * Render a fixed bottom banner describing a failed Livewire request.
 * Uses inline styles so it works regardless of which Tailwind classes
 * happen to be compiled into the current page's stylesheet.
 * Only one banner is shown at a time; calling again replaces it.
 */
function showLivewireErrorBanner(details) {
    document.getElementById('livewire-error-banner')?.remove();

    const banner = document.createElement('div');
    banner.id = 'livewire-error-banner';
    banner.style.cssText = css({
        position: 'fixed',
        bottom: '1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        'z-index': '99999',
        display: 'flex',
        'flex-direction': 'column',
        gap: '0.75rem',
        padding: '1rem 1.25rem',
        background: '#1e293b',
        color: '#f8fafc',
        'border-radius': '0.5rem',
        'box-shadow': '0 10px 25px rgba(0,0,0,0.3)',
        'font-family': 'inherit',
        'font-size': '0.875rem',
        'line-height': '1.4',
        'max-width': 'min(640px, calc(100vw - 2rem))',
        width: 'max-content',
    });

    banner.appendChild(buildHeader(details));

    if (details.server?.message) {
        banner.appendChild(buildServerMessage(details.server));
    }

    const detailsPanel = buildDetailsList(details);
    banner.appendChild(detailsPanel);

    banner.appendChild(buildActions(details, detailsPanel));

    document.body.appendChild(banner);
}

function buildHeader(details) {
    const header = document.createElement('div');
    header.style.cssText = css({
        display: 'flex',
        'align-items': 'baseline',
        'justify-content': 'space-between',
        gap: '1rem',
    });

    const title = document.createElement('div');
    title.style.cssText = css({ 'font-weight': '600', 'font-size': '0.9375rem' });
    title.textContent = `Error ${details.status}: ${details.statusText}`;

    const ref = document.createElement('div');
    ref.style.cssText = css({ color: '#94a3b8', 'font-size': '0.75rem', 'font-family': 'ui-monospace, monospace' });
    ref.textContent = `ref ${details.referenceId}`;

    header.appendChild(title);
    header.appendChild(ref);
    return header;
}

function buildServerMessage(server) {
    const box = document.createElement('div');
    box.style.cssText = css({
        background: '#0f172a',
        color: '#fca5a5',
        padding: '0.5rem 0.75rem',
        'border-radius': '0.375rem',
        'font-family': 'ui-monospace, SFMono-Regular, Menlo, monospace',
        'font-size': '0.8125rem',
        'white-space': 'pre-wrap',
        'word-break': 'break-word',
        'max-height': '8rem',
        'overflow-y': 'auto',
    });
    // Show only the exception class + message in the visible banner. Absolute
    // server paths (server.file/server.line) stay out of both the UI *and* the
    // clipboard copy (users paste that into public support tickets). They are
    // kept only in the Honeybadger context where they reach engineers directly.
    box.textContent = server.exception ? `${server.exception}: ${server.message}` : server.message;
    return box;
}

function buildDetailsList(details) {
    const list = document.createElement('div');
    list.style.cssText = css({
        display: 'none',
        'flex-direction': 'column',
        gap: '0.25rem',
        'font-size': '0.75rem',
        color: '#cbd5e1',
        'word-break': 'break-all',
    });

    const rows = [
        ['Time', details.timestamp],
        ['Page', details.pageUrl],
        ['Request', details.uri],
        ['Component', details.component],
        ['Action', details.calls?.length ? details.calls.join(', ') : null],
    ];

    for (const [label, value] of rows) {
        if (!value) {
            continue;
        }
        const row = document.createElement('div');
        const labelEl = document.createElement('span');
        labelEl.style.cssText = css({ color: '#94a3b8', 'margin-right': '0.5rem' });
        labelEl.textContent = `${label}:`;
        const valueEl = document.createElement('span');
        valueEl.textContent = value;
        row.appendChild(labelEl);
        row.appendChild(valueEl);
        list.appendChild(row);
    }

    return list;
}

function buildActions(details, detailsPanel) {
    const actions = document.createElement('div');
    actions.style.cssText = css({
        display: 'flex',
        gap: '0.5rem',
        'flex-wrap': 'wrap',
        'justify-content': 'flex-end',
    });

    actions.appendChild(bannerButton('Refresh', () => window.location.reload(), 'primary'));

    const toggle = bannerButton('Show details', () => {
        const shown = detailsPanel.style.display !== 'none';
        detailsPanel.style.display = shown ? 'none' : 'flex';
        toggle.textContent = shown ? 'Show details' : 'Hide details';
    });
    actions.appendChild(toggle);

    const copy = bannerButton('Copy details', async () => {
        const text = formatDetailsForClipboard(details);
        try {
            await navigator.clipboard.writeText(text);
            copy.textContent = 'Copied!';
            setTimeout(() => { copy.textContent = 'Copy details'; }, 1500);
        } catch (err) {
            // Permission denied, iframe sandboxing, or http origin typically;
            // surface to devtools but don't break the banner.
            console.warn('[Livewire error banner] Clipboard copy failed:', err);
            copy.textContent = 'Copy failed';
            setTimeout(() => { copy.textContent = 'Copy details'; }, 1500);
        }
    });
    actions.appendChild(copy);

    actions.appendChild(bannerButton('Dismiss', () => {
        document.getElementById('livewire-error-banner')?.remove();
    }));

    return actions;
}

function bannerButton(label, onClick, variant = 'secondary') {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    const base = {
        padding: '0.375rem 0.875rem',
        border: 'none',
        'border-radius': '0.375rem',
        cursor: 'pointer',
        'font-size': '0.8125rem',
        'font-weight': '600',
        'white-space': 'nowrap',
        'flex-shrink': '0',
    };
    const palette = variant === 'primary'
        ? { background: '#3b82f6', color: '#fff' }
        : { background: '#334155', color: '#f8fafc' };
    button.style.cssText = css({ ...base, ...palette });
    button.addEventListener('click', onClick);
    return button;
}

function formatDetailsForClipboard(details) {
    const lines = [
        `Reference: ${details.referenceId}`,
        `Time:      ${details.timestamp}`,
        `Status:    ${details.status} ${details.statusText}`,
        `Page:      ${details.pageUrl}`,
    ];
    if (details.uri) {
        lines.push(`Request:   ${details.uri}`);
    }
    if (details.component) {
        lines.push(`Component: ${details.component}`);
    }
    if (details.calls?.length) {
        lines.push(`Action:    ${details.calls.join(', ')}`);
    }
    if (details.server?.message) {
        const ex = details.server.exception ? `${details.server.exception}: ` : '';
        lines.push(`Message:   ${ex}${details.server.message}`);
        // Intentionally omit server.file/server.line: users paste this into
        // support tickets and those paths reveal infra. They live only in
        // the Honeybadger context where they reach engineers directly.
    }
    lines.push(`User Agent: ${details.userAgent}`);
    return lines.join('\n');
}

function css(declarations) {
    return Object.entries(declarations).map(([k, v]) => `${k}:${v}`).join(';');
}

/**
 * Reward tier card that blinks its $ amount to draw the user's eye to their next target.
 *
 * @param {boolean} isNext - only the next achievable tier animates; completed and future tiers stay static
 *
 * Respects prefers-reduced-motion: the interval is never started for users
 * who have requested reduced motion in their OS/browser settings.
 */
Alpine.data('blinkingTier', (isNext = false) => ({
    show: true,
    blinkInterval: null,
    init() {
        if (!isNext || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        this.blinkInterval = setInterval(() => { this.show = !this.show; }, 1500);
    },
    destroy() {
        if (this.blinkInterval !== null) {
            clearInterval(this.blinkInterval);
        }
    },
}));

// ── Team Trips v2 Alpine components ──────────────────────────────────────────
// Registered here so they are available globally across all pages without
// lazy-loading complexity. The modules themselves gate on prefers-reduced-motion
// internally; idle cost is negligible when the component is not mounted.

/** Hero image crossfade + Ken-Burns (A1, A2 — SPEC_motion.md). */
Alpine.data('heroCrossfade', heroCrossfade);

/** Customs-stamp auto-rotate + active glow (A13, A14, A15 — SPEC_motion.md). */
Alpine.data('customsStamps', customsStamps);

/** Destination marquee pause-on-hover (A12 — SPEC_motion.md). */
Alpine.data('marquee', marquee);

/** Mobile bottom-sheet summary drawer (< tt-xl / 1080px breakpoint). */
Alpine.data('mobileDrawer', mobileDrawer);

/** IntersectionObserver that calls $wire.recordStepViewed(n) on scroll. */
Alpine.data('stepObserver', stepObserver);

Livewire.start();
